<template>
  <div>
    <v-dialog
      v-model="showDialog"
      content-class="assistance-guide-dialog-container"
      persistent
      :max-width="isMobile ? '90dvw' : '460px'"
    >
      <v-card v-if="guide" class="assistance-guide-card">
        <v-icon class="card-close" color="black" @click.stop="dismiss">{{
          icons.mdiClose
        }}</v-icon>
        <div class="card-inner">
          <div class="header-row">
            <v-icon color="#2e7d32" class="header-icon">{{
              icons.mdiAlertCircleOutline
            }}</v-icon>
            <strong class="header-text">{{ guide.content.title }}</strong>
          </div>
          <p
            class="body-text"
            v-for="(paragraph, index) in bodyParagraphs"
            :key="index"
            v-html="paragraph"
          />
          <div v-if="showLanguageSelect" class="language-select">
            <strong class="language-select-label">Select a language</strong>
            <RadioSelect
              v-model="selectedLanguage"
              :items="languageOptions"
              :group="'assistance_will_language'"
              :flexColumn="true"
              :color="'primary'"
            />
            <div v-if="showEmailOption" class="email-copy-option">
              <Checkbox
                v-model="emailCopy"
                :label="'Email me a copy of the will'"
                color="primary"
              />
            </div>
          </div>
          <div
            v-if="guide.content.actionText || guide.content.secondaryActionText"
            class="action-container"
          >
            <GradientButton
              v-if="guide.content.actionText"
              :title="guide.content.actionText"
              :callout="'right'"
              :disabled="showLanguageSelect && !selectedLanguage"
              @gradientButtonClicked="onAction"
            />
            <GradientButton
              v-if="guide.content.secondaryActionText"
              :title="guide.content.secondaryActionText"
              :callout="'right'"
              :fill="false"
              @gradientButtonClicked="onSecondaryAction"
            />
          </div>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import { mdiClose, mdiAlertCircleOutline } from '@mdi/js';
  import { EventBus } from '../../../constants/js/event-bus';
  import RadioSelect from '../components/RadioSelect.vue';

  const CODE_TO_LANGUAGE = {
    en: 'ENGLISH',
    af: 'AFRIKAANS',
    xh: 'XHOSA',
    zu: 'ZULU',
    nso: 'SOTHO',
  };

  export default {
    name: 'TestatorAssistanceGuide',
    components: {
      RadioSelect,
    },
    data() {
      return {
        showDialog: false,
        delayTimer: null,
        selectedLanguage: 'ENGLISH',
        emailCopy: true,
        allLanguageOptions: [
          { value: 'ENGLISH', text: 'English', code: 'en' },
          { value: 'AFRIKAANS', text: 'Afrikaans', code: 'af' },
          { value: 'XHOSA', text: 'Xhosa', code: 'xh' },
          { value: 'ZULU', text: 'Zulu', code: 'zu' },
          { value: 'SOTHO', text: 'Sotho (Sepedi)', code: 'nso' },
        ],
        icons: {
          mdiClose: mdiClose,
          mdiAlertCircleOutline: mdiAlertCircleOutline,
        },
      };
    },
    computed: {
      guide() {
        return this.$store.getters.activeGuide;
      },
      hasActiveGuide() {
        return this.$store.getters.hasActiveGuide;
      },
      bodyParagraphs() {
        if (!this.guide) return [];
        return this.guide.content.body
          .split('\n\n')
          .map(p => p.trim())
          .filter(p => p.length > 0)
          .map(p => p.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>'));
      },
      languageOptions() {
        const codes = this.guide && this.guide.content.languageCodes;
        if (!codes || !codes.length) return this.allLanguageOptions;
        return this.allLanguageOptions.filter(o => codes.includes(o.code));
      },
      showLanguageSelect() {
        return (
          !!(this.guide && this.guide.content.showLanguageSelect) &&
          this.languageOptions.length > 1
        );
      },
      // "Email me a copy of the will" is only offered to an adviser using
      // "log in as testator", and only alongside the language selection.
      showEmailOption() {
        return (
          this.showLanguageSelect &&
          !!this.$store.getters.isLoggedInAsSomeoneElse &&
          this.$store.getters.role === 'advisor'
        );
      },
      isMobile() {
        return this.$isMobileDevice();
      },
    },
    watch: {
      hasActiveGuide: {
        immediate: true,
        handler(val) {
          clearTimeout(this.delayTimer);
          if (val) {
            this.resetSelectedLanguage();
            // Never persisted: starts checked every time a guide is shown.
            this.emailCopy = true;
            this.delayTimer = setTimeout(() => {
              if (this.hasActiveGuide && !this.isAnotherDialogOpen()) {
                this.showDialog = true;
              }
            }, 1000);
          } else {
            this.showDialog = false;
          }
        },
      },
    },
    beforeDestroy() {
      clearTimeout(this.delayTimer);
    },
    methods: {
      isAnotherDialogOpen() {
        const overlays = document.querySelectorAll('.v-overlay--active');
        return overlays.length > 0;
      },
      resetSelectedLanguage() {
        const defaultCode =
          (this.guide && this.guide.content.defaultLanguageCode) || 'en';
        const preferred = CODE_TO_LANGUAGE[defaultCode];
        const inList = this.languageOptions.find(o => o.value === preferred);
        this.selectedLanguage = inList
          ? preferred
          : (this.languageOptions[0] && this.languageOptions[0].value) ||
            'ENGLISH';
      },
      dismiss() {
        this.showDialog = false;
        this.$store.dispatch('dismissCurrentGuide');
      },
      onAction() {
        const { actionType, actionEvent, actionRoute } = this.guide.content;
        const useLanguageSelect = this.showLanguageSelect;
        const language = this.selectedLanguage;
        const emailTestator = this.showEmailOption ? this.emailCopy : true;
        this.showDialog = false;
        this.$store.dispatch('dismissCurrentGuide');
        if (actionType === 'dismiss') {
          return;
        } else if (useLanguageSelect && language) {
          EventBus.$emit(
            'trigger-download-will-with-language',
            language,
            emailTestator
          );
        } else if (actionType === 'event' && actionEvent) {
          EventBus.$emit(actionEvent);
        } else if (actionRoute) {
          this.$router.push(actionRoute);
        }
      },
      onSecondaryAction() {
        const {
          secondaryActionType,
          secondaryActionEvent,
          secondaryActionRoute,
        } = this.guide.content;
        this.showDialog = false;
        this.$store.dispatch('dismissCurrentGuide');
        if (secondaryActionType === 'event' && secondaryActionEvent) {
          EventBus.$emit(secondaryActionEvent);
        } else if (secondaryActionRoute) {
          this.$router.push(secondaryActionRoute);
        }
      },
    },
  };
</script>

<style lang="scss">
  .assistance-guide-dialog-container {
    border-radius: 28px !important;
    overflow: hidden;
    background: transparent !important;
    box-shadow: none !important;

    .assistance-guide-card {
      position: relative;
      border-radius: 28px !important;
      padding: 30px 30px 40px 30px;

      .card-close {
        position: absolute;
        top: 24px;
        right: 24px;
        cursor: pointer;
      }

      .card-inner {
        .header-row {
          display: flex !important;
          flex-direction: row !important;
          flex-wrap: nowrap !important;
          align-items: center;
          gap: 8px;
          margin-bottom: 16px;
          padding-right: 32px;

          .header-icon {
            font-size: 24px;
            flex-shrink: 0;
          }

          .header-text {
            font-family: 'Montserrat', sans-serif;
            font-size: 22px;
            font-weight: 600;
            line-height: 100%;
            color: #000;
          }
        }

        .body-text {
          font-size: 15px;
          font-weight: 400;
          line-height: 22px;
          color: #333;
          padding-top: 16px;
          margin-bottom: 12px;

          strong {
            font-family: 'Montserrat', sans-serif;
            font-weight: 600;
            font-size: 16px;
            line-height: 22px;
          }

          &:last-of-type {
            padding-top: 0;
            margin-bottom: 0;
          }
        }

        .language-select {
          margin-top: 24px;

          .language-select-label {
            font-family: 'Montserrat', sans-serif;
            font-weight: 600;
            font-size: 16px;
            line-height: 22px;
            color: #000;
          }

          // Visually separate the email option from the language radio list
          .email-copy-option {
            margin-top: 32px;
            padding-top: 16px;
            border-top: 1px solid rgba(0, 0, 0, 0.12);

            .omk-checkbox-container {
              margin: 0;
            }

            .v-input--checkbox {
              margin-top: 0;
              padding-top: 0;
            }
          }
        }

        .action-container {
          display: flex;
          gap: 12px;
          margin-top: 40px;
          flex-wrap: wrap;
        }
      }
    }
  }
</style>
